/**
 * E10 — Unified Application Detail: Attribute Dictionary
 *
 * Single source of truth for every Application attribute we surface in the UI.
 * Drives: labels, plain-language tooltips, copy affordances, progressive
 * disclosure (defaultVisible), and source-object tagging (app reg vs SP vs SSOps).
 *
 * Adding a new field: add an entry here and the AttributeField component in
 * ApplicationDetail picks it up automatically — no scatter-gunned label strings.
 */

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

/** Where the data originates. Used for integration-shape awareness (AD3):
 *  hide app_registration fields when the app has no app registration object. */
export type AttributeSource = 'app_registration' | 'enterprise_app' | 'ssops'

/** How the value should be rendered. AttributeField uses this hint. */
export type AttributeFormat =
  | 'text'       // plain string
  | 'monospace'  // IDs, GUIDs, thumbprints
  | 'date'       // ISO string → locale date/time
  | 'url'        // clickable anchor
  | 'boolean'    // Yes / No badge
  | 'badge'      // colored pill (appType, signInAudience)

export interface AttributeDef {
  /** Short label shown above the value. */
  label: string
  /** Plain-language explanation shown in the (?) tooltip.
   *  Write for an IT admin who knows their domain but not Azure jargon. */
  description: string
  /** Which object in Entra the data comes from.
   *  'ssops' = stored/managed entirely within SSOps (not from Graph). */
  source: AttributeSource
  /** Show a copy-to-clipboard button next to the value. */
  copyable?: boolean
  /** Mask the value behind a "reveal" toggle (future use — secrets/certs). */
  sensitive?: boolean
  /** Shown in the default view. Set false to put the field behind "Show more". */
  defaultVisible?: boolean
  /** Rendering hint for AttributeField. Defaults to 'text'. */
  format?: AttributeFormat
}

// --------------------------------------------------------------------------
// Dictionary
// --------------------------------------------------------------------------

/**
 * Keys must match field names on the Application interface in types/index.ts.
 * Fields not listed here have no tooltip or copy support and render as plain text.
 */
export const APPLICATION_ATTRIBUTES = {

  // ── App Registration ────────────────────────────────────────────────────

  entraAppId: {
    label: 'Application (Client) ID',
    description:
      'The unique ID Azure uses to identify this app registration. ' +
      'Copy this when configuring MSAL, SAML SP metadata, or any tool ' +
      'that asks "what is the client ID?"',
    source: 'app_registration',
    copyable: true,
    defaultVisible: true,
    format: 'monospace',
  },

  displayName: {
    label: 'Display Name',
    description:
      'The name shown to users in sign-in prompts and in the Azure portal. ' +
      'Synced from Entra ID — change it there, not here.',
    source: 'app_registration',
    defaultVisible: true,
    format: 'text',
  },

  description: {
    label: 'Description',
    description:
      'A short summary of what this application does and who uses it. ' +
      'Edit this in SSOps; it is stored locally and is not written back to Entra.',
    source: 'ssops',
    defaultVisible: true,
    format: 'text',
  },

  signInAudience: {
    label: 'Sign-in Audience',
    description:
      '"AzureADMyOrg" → only users in your tenant can sign in. ' +
      '"AzureADMultipleOrgs" → any Azure AD tenant. ' +
      '"AzureADandPersonalMicrosoftAccount" → also personal Microsoft accounts. ' +
      'Most internal apps should be AzureADMyOrg.',
    source: 'app_registration',
    defaultVisible: true,
    format: 'badge',
  },

  homepage: {
    label: 'Homepage URL',
    description:
      'Where users land after a successful sign-in. Also used as the base URL ' +
      'for SAML single sign-on in some configurations.',
    source: 'app_registration',
    copyable: true,
    defaultVisible: true,
    format: 'url',
  },

  isVerifiedPublisher: {
    label: 'Verified Publisher',
    description:
      'Microsoft has confirmed the publisher\'s identity via the Partner Network. ' +
      'Internal or first-party apps typically won\'t have this badge — its absence ' +
      'is not a security risk on its own.',
    source: 'app_registration',
    defaultVisible: false,
    format: 'boolean',
  },

  // ── Enterprise App (Service Principal) ──────────────────────────────────

  appType: {
    label: 'SSO Mode',
    description:
      'The SSO protocol this integration uses. SAML is the traditional enterprise ' +
      'standard; OIDC is the modern OAuth-based approach. "Disabled" means the app ' +
      'is registered but SSO is not configured.',
    source: 'enterprise_app',
    defaultVisible: true,
    format: 'badge',
  },

  // ── SSOps-managed ────────────────────────────────────────────────────────

  businessUnitName: {
    label: 'Business Unit',
    description:
      'The team or department responsible for this integration. Assigned in SSOps ' +
      'and not synced back to Entra. Used for filtering, reporting, and ownership routing.',
    source: 'ssops',
    defaultVisible: true,
    format: 'text',
  },

  riskLevelName: {
    label: 'Risk Level',
    description:
      'SSOps classification of how sensitive this integration is, based on data ' +
      'sensitivity and blast radius. Drives alert priority and review cadence.',
    source: 'ssops',
    defaultVisible: true,
    format: 'badge',
  },

  healthScore: {
    label: 'Health Score',
    description:
      'A 0–100 score computed from open security findings: missing owners, expired ' +
      'credentials, absent CA coverage, and more. Lower scores need attention first.',
    source: 'ssops',
    defaultVisible: true,
    format: 'text',
  },

  firstDiscoveredAt: {
    label: 'First Discovered',
    description:
      'When SSOps first saw this app during a sync from Entra ID. ' +
      'Apps discovered recently may not have full ownership or classification yet.',
    source: 'ssops',
    defaultVisible: false,
    format: 'date',
  },

  lastSyncedAt: {
    label: 'Last Synced',
    description:
      'The most recent time SSOps pulled fresh data for this app from Entra ID. ' +
      'Syncs run automatically every night; you can also trigger one manually from Settings.',
    source: 'ssops',
    defaultVisible: true,
    format: 'date',
  },

}

export type ApplicationAttributeKey = keyof typeof APPLICATION_ATTRIBUTES
